import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Loader2, Upload, Trash2, LogOut, Image as ImageIcon, Film, ArrowLeft, Lock,
} from "lucide-react";
import {
  API, FILE_URL, authHeader, getToken, clearToken, login,
  chunkedUpload, saveCreds, loadCreds, clearCreds,
} from "@/lib/api";
import { AdminLiveControl } from "@/components/site/AdminLiveControl";
import { AdminCaption } from "@/components/site/AdminCaption";

export default function Admin() {
  const [authed, setAuthed] = useState(null); // null=checking
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);

  useEffect(() => {
    const saved = loadCreds();
    if (saved) {
      setEmail(saved.email || "");
      setPassword(saved.password || "");
      setRemember(true);
    }
  }, []);

  const checkSession = useCallback(async () => {
    if (!getToken()) { setAuthed(false); return; }
    try {
      await axios.get(`${API}/auth/me`, { headers: authHeader() });
      setAuthed(true);
    } catch {
      clearToken();
      setAuthed(false);
    }
  }, []);

  useEffect(() => { checkSession(); }, [checkSession]);

  const doLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      await login(email, password);
      if (remember) saveCreds(email, password);
      else clearCreds();
      setAuthed(true);
      toast.success("Welcome back, admin.");
    } catch {
      toast.error("Invalid email or password.");
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => { clearToken(); setAuthed(false); };

  if (authed === null) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="animate-spin text-green" size={40} />
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-5">
        <motion.form
          onSubmit={doLogin}
          data-testid="admin-login-form"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md border border-[#1a1a1a] bg-[#0a0a0a] p-8 md:p-10"
        >
          <div className="flex items-center gap-3 mb-2">
            <Lock className="text-green" size={22} />
            <span className="label">Admin Access</span>
          </div>
          <h1 className="font-display uppercase text-4xl text-white mb-8">
            Mask <span className="text-green glow-green">Giver</span>
          </h1>
          <input
            data-testid="admin-email"
            type="email"
            className="field mb-6"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <input
            data-testid="admin-password"
            type="password"
            className="field mb-5"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <label className="flex items-center gap-3 mb-8 cursor-pointer select-none" data-testid="admin-remember-label">
            <button
              type="button"
              data-testid="admin-remember"
              onClick={() => setRemember((r) => !r)}
              className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${remember ? "bg-green" : "bg-[#222]"}`}
              aria-pressed={remember}
              aria-label="Remember me"
            >
              <span className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-black transition-transform ${remember ? "translate-x-5" : ""}`} />
            </button>
            <span className="font-sub text-sm text-zinc-400">Remember me on this device</span>
          </label>
          <button
            data-testid="admin-login-submit"
            disabled={loggingIn}
            className="btn-fill w-full border border-green text-green font-sub font-semibold text-lg py-3.5 rounded-full flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loggingIn ? <Loader2 className="animate-spin" size={20} /> : <Lock size={18} />}
            {loggingIn ? "Signing in…" : "Sign in"}
          </button>
          <Link to="/" className="mt-6 flex items-center gap-2 justify-center text-zinc-500 hover:text-green font-sub text-sm">
            <ArrowLeft size={15} /> Back to site
          </Link>
        </motion.form>
      </div>
    );
  }

  return <AdminDashboard onLogout={logout} />;
}

function AdminDashboard({ onLogout }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [section, setSection] = useState("gallery");
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/media`);
      setItems(data);
    } catch {
      toast.error("Couldn't load media.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doUpload = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose a photo or video first."); return; }
    const MAX = 2 * 1024 * 1024 * 1024;
    if (file.size > MAX) { toast.error("File too large (max 2GB)."); return; }
    setUploading(true);
    setProgress(0);
    try {
      await chunkedUpload(file, section, caption, setProgress);
      toast.success("Uploaded! It's now live on the site.");
      setFile(null);
      setCaption("");
      setProgress(0);
      e.target.reset();
      load();
    } catch (err) {
      toast.error(err.response?.status === 413 ? "File too large (max 2GB)." : "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/media/${id}`, { headers: authHeader() });
      setItems((prev) => prev.filter((m) => m.id !== id));
      toast.success("Removed.");
    } catch {
      toast.error("Couldn't delete.");
    }
  };

  const gallery = items.filter((m) => m.section === "gallery");
  const kindness = items.filter((m) => m.section === "kindness");
  const logo = items.filter((m) => m.section === "logo");

  return (
    <div className="min-h-screen bg-[#050505]">
      <header className="border-b border-[#1a1a1a] sticky top-0 bg-black/80 backdrop-blur-xl z-20">
        <div className="mx-auto max-w-[1200px] px-5 md:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-display text-2xl"><span className="text-white">M</span><span className="text-green">G</span></span>
            <span className="label">Admin · Media Studio</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="font-sub text-sm text-zinc-400 hover:text-green">View site</Link>
            <button data-testid="admin-logout" onClick={onLogout} className="flex items-center gap-2 border border-[#1a1a1a] hover:border-green text-white px-4 py-2 rounded-full font-sub text-sm">
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1200px] px-5 md:px-8 py-12">
        <AdminLiveControl />
        <AdminCaption />

        <form onSubmit={doUpload} data-testid="admin-upload-form" className="border border-[#1a1a1a] bg-[#0a0a0a] p-6 md:p-8 mb-14">
          <h2 className="font-display uppercase text-3xl text-white mb-6">
            Upload <span className="text-green">photo / video</span>
          </h2>

          <div className="flex gap-2 mb-6 border border-[#1a1a1a] p-1 rounded-full w-fit flex-wrap">
            {[
              { id: "gallery", label: "Add to Gallery" },
              { id: "kindness", label: "Kindness reel" },
              { id: "logo", label: "Logo (image / intro video)" },
            ].map((t) => (
              <button
                type="button"
                key={t.id}
                data-testid={`admin-section-${t.id}`}
                onClick={() => setSection(t.id)}
                className={`font-sub text-sm px-5 py-2 rounded-full transition-colors ${
                  section === t.id ? "bg-green text-black font-semibold" : "text-zinc-400 hover:text-white"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <label className="block border-2 border-dashed border-[#222] hover:border-green/60 transition-colors rounded-xl p-10 text-center cursor-pointer mb-6">
            <input
              data-testid="admin-file-input"
              type="file"
              accept="image/*,video/*"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
            <Upload className="mx-auto text-green mb-3" size={30} />
            <p className="font-sub text-white">{file ? file.name : "Click to choose an image or video"}</p>
            <p className="font-sub text-xs text-zinc-500 mt-1">JPG, PNG, WEBP, MP4, MOV, WEBM · videos up to 2GB (~30 min)</p>
          </label>

          <input
            data-testid="admin-upload-caption"
            className="field mb-6"
            placeholder="Caption (optional)"
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
          />

          {uploading && (
            <div className="mb-6" data-testid="upload-progress">
              <div className="flex justify-between font-sub text-xs text-zinc-400 mb-2">
                <span>Uploading{file ? ` — ${file.name}` : ""}</span>
                <span className="text-green">{progress}%</span>
              </div>
              <div className="w-full h-2 bg-[#1a1a1a] rounded-full overflow-hidden">
                <div className="h-full bg-green transition-all duration-200" style={{ width: `${progress}%` }} />
              </div>
              <p className="font-sub text-xs text-zinc-600 mt-2">Large files upload in chunks — keep this tab open until it finishes.</p>
            </div>
          )}

          <button
            data-testid="admin-upload-submit"
            disabled={uploading}
            className="btn-fill border border-green text-green font-sub font-semibold text-lg px-8 py-3.5 rounded-full flex items-center gap-2 disabled:opacity-60"
          >
            {uploading ? <Loader2 className="animate-spin" size={20} /> : <Upload size={18} />}
            {uploading ? `Uploading ${progress}%` : "Upload"}
          </button>
        </form>

        <MediaGroup title="Logo & intro video" icon={<Film size={18} />} items={logo} loading={loading} onDelete={remove} testid="admin-logo-list" />
        <MediaGroup title="Kindness reel" icon={<Film size={18} />} items={kindness} loading={loading} onDelete={remove} testid="admin-kindness-list" />
        <MediaGroup title="Gallery media" icon={<ImageIcon size={18} />} items={gallery} loading={loading} onDelete={remove} testid="admin-gallery-list" />
      </main>
    </div>
  );
}

function MediaGroup({ title, icon, items, loading, onDelete, testid }) {
  return (
    <section className="mb-14" data-testid={testid}>
      <div className="flex items-center gap-2 mb-5 text-green">{icon}<span className="label !text-green">{title}</span><span className="text-zinc-600 font-sub text-sm">({items.length})</span></div>
      {loading ? (
        <Loader2 className="animate-spin text-zinc-600" />
      ) : items.length === 0 ? (
        <p className="font-sub text-zinc-600">Nothing here yet.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((m) => (
            <div key={m.id} className="group relative border border-[#1a1a1a] bg-[#0a0a0a] overflow-hidden">
              {m.media_type === "video" ? (
                <video src={FILE_URL(m.url)} className="w-full h-40 object-cover" muted playsInline controls />
              ) : (
                <img src={FILE_URL(m.url)} alt={m.caption || "media"} className="w-full h-40 object-cover" />
              )}
              <div className="p-3">
                <p className="font-sub text-xs text-zinc-400 truncate">{m.caption || m.media_type}</p>
              </div>
              <button
                data-testid={`admin-delete-${m.id}`}
                onClick={() => onDelete(m.id)}
                className="absolute top-2 right-2 bg-black/70 hover:bg-red-600 text-white p-2 rounded-full transition-colors"
                aria-label="Delete"
              >
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
